import React, {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import SelectedCapabilityList from './SelectedCapabilityList';
import { Button } from 'reactstrap';
import { Icon } from 'js/components';
import { faListTree } from '@fortawesome/pro-regular-svg-icons';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import { useEditorContext } from 'js/ydoc/docs/capabilities-assessment/EditorContext';
import PageBadge from '../PageBadge';

import { EditorInput } from 'js/components/Assessment/Editors/common/components/EditorInput';
import { useCollaboratorForField } from 'js/ydoc/doc/hooks';
import CapabilityQuestion from '../../Questions/CapabilityQuestion';
import CapabilitySelector from './CapabilitySelector/CapabilitySelector';
import ErrorMessage from '../../../common/components/EditorErrors/ErrorMessage';

import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

import PageActions from '../PageActions';
import PageTitleError from '../PageTitleError';
import PageHeaderToggle from '../PageHeaderToggle';
import { shouldRenderPageHeader } from 'js/engines/CapabilitiesAssessment/utils';
import DropIndicator from 'js/components/Assessment/Editors/common/components/DropIndicator';
import Element from './Element';

import styles from '../../CapabilitiesAssessmentEditor.module.scss';

function DragPreview({ page, capabilitySet, pageBadgeIndices, width }) {
  const rootClassName = useMemo(() => {
    return classNames(styles.page, styles['page-capabilities']);
  }, []);

  return (
    <div className={rootClassName} style={{ width }}>
      <div className={styles['page-content']}>
        <div className={styles['page-header']}>
          <div className={styles['drag-handle']}>
            <button className="drag-handle">
              <Icon icon={faGripVertical} className="icon" />
            </button>
          </div>
          <div className={styles['capability-set-badge']}>
            <PageBadge
              lookupValue={page.capabilitySetId}
              pageBadgeIndices={pageBadgeIndices}
            >
              {capabilitySet?.name ?? 'No capability set selected'}
            </PageBadge>
          </div>
          <div className={styles['page-guid']}>{page.guid}</div>
        </div>
        <div className={styles['page-title']}>{page.title}</div>
        <div className={styles['page-description']}>{page.description}</div>
      </div>
    </div>
  );
}

function CapabilitiesPage({ page }) {
  const {
    updatePageFields,
    deleteCapabilityElement,
    cachedCapabilitySets,
    addCapabilityElementToPage,
    pageBadgeIndices,
    movePageAfter,
    movePageBefore,
    addElementToCapabilityPage,
  } = useEditorContext();

  const collaboratorForTitle = useCollaboratorForField(
    'page',
    page.guid,
    'title'
  );

  const collaboratorForDescription = useCollaboratorForField(
    'page',
    page.guid,
    'description'
  );

  const headerHidden = !shouldRenderPageHeader(page);

  const handleFieldChange = useCallback(
    ({ target: { name, value } }) => {
      updatePageFields(page.guid, { [name]: value });
    },
    [page.guid, updatePageFields]
  );

  const handleDeleteCapabilityQuestion = useCallback(
    (questionGuid) => {
      deleteCapabilityElement(page.guid, questionGuid);
    },
    [page.guid, deleteCapabilityElement]
  );

  const [showCapabilitySelector, setShowCapabilitySelector] = useState(false);
  const handleShowCapabilitySelector = useCallback(() => {
    setShowCapabilitySelector(true);
  }, []);
  const handleHideCapabilitySelector = useCallback(() => {
    setShowCapabilitySelector(false);
  }, []);

  const handleAddQuestion = useCallback(() => {
    addElementToCapabilityPage(page.guid, {
      type: 'long-text',
      text: 'Please include any additional comments to clarify your responses',
    });
  }, [page.guid, addElementToCapabilityPage]);

  const capabilitySet = useMemo(() => {
    return cachedCapabilitySets[page.capabilitySetId];
  }, [page.capabilitySetId, cachedCapabilitySets]);

  const ref = useRef(null);
  const dragHandleRef = useRef(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    return combine(
      draggable({
        element: ref.current,
        dragHandle: dragHandleRef.current,
        getInitialData: () => ({ page }),
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            render({ container }) {
              ReactDOM.render(
                <DragPreview
                  page={page}
                  capabilitySet={capabilitySet}
                  width={ref.current.scrollWidth}
                  pageBadgeIndices={pageBadgeIndices}
                />,
                container
              );
              return function cleanup() {
                ReactDOM.unmountComponentAtNode(container);
              };
            },
            getOffset: () => ({ x: 20, y: 16 }),
            nativeSetDragImage,
          });
        },
      }),
      dropTargetForElements({
        element: ref.current,
        getData: (args) => {
          return attachClosestEdge(
            { page },
            {
              input: args.input,
              element: args.element,
              allowedEdges: ['top', 'bottom'],
            }
          );
        },
        canDrop: ({ source }) =>
          !!source.data.capabilityElement ||
          (!!source.data.page && source.data.page.guid !== page.guid),
        onDragEnter: ({ source }) => {
          setIsDraggedOver(true);
          setIsDropTarget(
            source.data.capabilityElement &&
              !(
                source.data.capabilityElement.guid in
                page.capabilityElementPositions
              )
          );
        },
        // onDragLeave: () => {
        //   // setClosestEdge(null);
        //   setIsDraggedOver(false);
        // },
        onDrag({ self, source }) {
          const isSource = source.element === el;
          if (isSource || !source.data.page) {
            setClosestEdge(null);
            return;
          }

          const closestEdge = extractClosestEdge(self.data);

          setClosestEdge(closestEdge);
        },
        onDragLeave: () => {
          setIsDraggedOver(false);
          setIsDropTarget(false);
        },
        onDrop: ({ source, location }) => {
          setIsDraggedOver(false);
          setIsDropTarget(false);
          setClosestEdge(null);

          if (location.current.dropTargets.length > 1) {
            // A child element is the target and will handle this
            return;
          }

          if (source.data.capabilityElement != null) {
            const elementGuid = source.data.capabilityElement.guid;
            if (!page.capabilityElements.some((e) => e.guid === elementGuid)) {
              addCapabilityElementToPage(elementGuid, page.guid);
            }
          } else if (
            source.data.page != null &&
            source.data.page.guid !== page.guid
          ) {
            if (closestEdge === 'top') {
              movePageBefore(source.data.page.guid, page.guid);
            } else if (closestEdge === 'bottom') {
              movePageAfter(source.data.page.guid, page.guid);
            }
          }
        },
      })
    );
  }, [
    addCapabilityElementToPage,
    page,
    capabilitySet,
    pageBadgeIndices,
    movePageAfter,
    movePageBefore,
    closestEdge,
  ]);

  const rootClassName = useMemo(() => {
    return classNames(styles.page, styles['page-capabilities'], {
      'dragged-over': isDraggedOver,
      'drop-target': isDropTarget,
    });
  }, [isDraggedOver, isDropTarget]);

  return (
    <div className={rootClassName} ref={ref}>
      {isDraggedOver && closestEdge !== null && (
        <DropIndicator
          top={closestEdge === 'top'}
          bottom={closestEdge === 'bottom'}
        />
      )}
      <div className={styles['page-content']}>
        <div className={styles['page-header']}>
          <div className={styles['drag-handle']}>
            <button className="drag-handle">
              <Icon icon={faGripVertical} className="icon" />
            </button>
          </div>
          <div className={styles['capability-set-badge']}>
            <PageBadge
              lookupValue={page.capabilitySetId}
              onClick={handleShowCapabilitySelector}
            >
              {capabilitySet?.name ?? 'No capability set selected'}
            </PageBadge>
          </div>
          <div className={styles['page-guid']}>{page.guid}</div>
          <PageHeaderToggle page={page} />
        </div>
        <div
          className={classNames(styles['page-title'], {
            [styles['page-header-hidden']]: headerHidden,
          })}
        >
          <EditorInput
            name="title"
            value={page.title}
            onChange={handleFieldChange}
            collaborator={collaboratorForTitle}
            placeholder="Add a page title"
          />
        </div>
        <PageTitleError title={page.title} />
        <div
          className={classNames(styles['page-description'], {
            [styles['page-header-hidden']]: headerHidden,
          })}
        >
          <EditorInput
            multiLine
            name="description"
            value={page.description || ''}
            onChange={handleFieldChange}
            collaborator={collaboratorForDescription}
            placeholder="Add a description"
          />
        </div>
        <section className={styles['capabilities-list']}>
          <div className={styles['capabilities-list-header']}>
            <div>
              <label>Capabilities</label>
            </div>
            <div className={styles['capabilities-list-header-actions']}>
              <Button
                color="primary"
                className="btn-action-alt"
                onClick={handleShowCapabilitySelector}
              >
                <Icon className="icon" icon={faListTree} />
              </Button>
            </div>
          </div>
          <div>
            <SelectedCapabilityList page={page} />
            <CapabilitySelector
              page={page}
              isOpen={showCapabilitySelector}
              onClose={handleHideCapabilitySelector}
            />
          </div>
        </section>
        <section className={styles['page-elements']}>
          <label>Capability questions</label>
          <div>
            {page.capabilityElements?.length === 0 && (
              <div className="invalid-feedback force-show">
                <ErrorMessage>
                  At least one capability question must be included
                </ErrorMessage>
              </div>
            )}
            {page.capabilityElements?.map((e) => (
              <CapabilityQuestion
                pageGuid={page.guid}
                key={e.guid}
                question={e}
                onDelete={handleDeleteCapabilityQuestion}
              />
            ))}
          </div>
        </section>
        <section className={styles['page-elements']}>
          <label>Additional questions</label>
          <div>
            {page.additionalElements?.map((e) => (
              <Element key={e.guid} page={page} element={e} />
            ))}
          </div>
          <div>
            <Button color="primary" outline onClick={handleAddQuestion}>
              Add Question
            </Button>
          </div>
        </section>
        <section className={styles['page-footer']}>
          <div>{closestEdge}</div>
          <PageActions page={page} />
        </section>
      </div>
    </div>
  );
}

export default CapabilitiesPage;
